// ═══ API CLIENT ═══
// Central API wrapper — handles auth tokens, error responses

const API_BASE = process.env.REACT_APP_API_URL || '/api';

let authToken = localStorage.getItem('gdnrent_token');

export function setToken(token) {
  authToken = token;
  if (token) localStorage.setItem('gdnrent_token', token);
  else localStorage.removeItem('gdnrent_token');
}

export function getToken() { return authToken; }

export function getStoredUser() {
  const u = localStorage.getItem('gdnrent_user');
  return u ? JSON.parse(u) : null;
}

export function setStoredUser(user) {
  if (user) localStorage.setItem('gdnrent_user', JSON.stringify(user));
  else localStorage.removeItem('gdnrent_user');
}

async function request(path, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    if (res.status === 401) { setToken(null); setStoredUser(null); window.location.reload(); }
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data;
}

// ── Auth ──
export const api = {
  login: (username, password) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),

  // ── Godowns ──
  getGodowns: (month) =>
    request(`/godowns${month ? `?month=${month}` : ''}`),

  createGodown: (data) =>
    request('/godowns', { method: 'POST', body: JSON.stringify(data) }),

  editGodown: (godownId, vendorId, month, changes) =>
    request(`/godowns/${godownId}/${vendorId}`, { method: 'PUT', body: JSON.stringify({ month, changes }) }),

  deleteGodown: (godownId, vendorId) =>
    request(`/godowns/${godownId}/${vendorId}`, { method: 'DELETE' }),

  uploadGodowns: (rows) =>
    request('/godowns/upload', { method: 'POST', body: JSON.stringify({ rows }) }),

  // ── Masters (branch / acode / bank / vendor) ──
  getMasters: () => request('/masters'),

  createMaster: (type, data) =>
    request(`/masters/${type}`, { method: 'POST', body: JSON.stringify(data) }),

  // ── Confirmations ──
  saveConfirmations: (confirmations) =>
    request('/confirmations', { method: 'POST', body: JSON.stringify({ confirmations }) }),

  resetConfirmation: (godownId, vendorId, month) =>
    request('/confirmations/reset', { method: 'POST', body: JSON.stringify({ godownId, vendorId, month }) }),

  // ── Cutoffs ──
  getCutoffs: () => request('/cutoffs'),

  setCutoff: (month, datetime) =>
    request(`/cutoffs/${month}`, { method: 'PUT', body: JSON.stringify({ datetime }) }),

  uploadCutoffs: (rows) =>
    request('/cutoffs/upload', { method: 'POST', body: JSON.stringify({ rows }) }),

  resetCutoffs: () =>
    request('/cutoffs/reset', { method: 'POST' }),

  // ── Users ──
  getUsers: () => request('/users'),

  createUser: (data) =>
    request('/users', { method: 'POST', body: JSON.stringify(data) }),

  updateUser: (userId, data) =>
    request(`/users/${userId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteUser: (userId) =>
    request(`/users/${userId}`, { method: 'DELETE' }),

  autoCreateUsers: () =>
    request('/users/auto-create', { method: 'POST' }),

  // ── Reports ──
  getReport: (params) => {
    const q = new URLSearchParams();
    if (params.month) q.set('month', params.month);
    if (params.branch) q.set('branch', params.branch);
    if (params.bank) q.set('bank', params.bank);
    if (params.issues) q.set('issues', params.issues);
    return request(`/reports?${q.toString()}`);
  },

  // ── Backup / Restore ──
  exportBackup: () => request('/admin/backup'),

  restoreBackup: (password, backup) =>
    request('/admin/restore', { method: 'POST', body: JSON.stringify({ password, backup }) }),

  // ── Admin ──
  wipeData: (password, mode) =>
    request('/admin/wipe', { method: 'POST', body: JSON.stringify({ password, mode }) }),
};
